import { readFromDocument as readUtmCookie } from '@enter/utm';
import { getConsentSnapshot } from '@/contexts/consent/store';
import { waitForInitialContent } from '@/lib/initial-content';

type MixpanelApi = typeof import('mixpanel-browser')['default'];
export type TrackProps = Record<string, unknown>;
export interface MixpanelTrackOptions {
    /** 由公开 logger 按现有 tab 会话合同去重，不由底层 SDK 排队实现。 */
    onceKey?: string;
    /** 立即交给 SDK 发送；不承诺服务端收到。 */
    sendImmediately?: boolean;
    /** 终态事件必须提供稳定的 insertId。 */
    kind?: 'interaction' | 'terminal';
    /** 原样映射到 Mixpanel $insert_id。 */
    insertId?: string;
}
type Operation =
    | { kind: 'event'; name: string; props?: TrackProps; options?: MixpanelTrackOptions; time: number }
    | { kind: 'people'; props: TrackProps; time: number }
    | { kind: 'register'; props: TrackProps; time: number };

export interface MixpanelClientConfig {
    /** 宿主构建信息，事件被接纳时写入，覆盖任何同名属性。 */
    eventMetadata?: TrackProps;
    /** 授权激活与身份重置后注册的 super properties；返回 null 表示没有可注册的值。 */
    superProperties?: () => TrackProps | null;
    /** 只延后 SDK 下载；已授权事件继续由现有队列接纳。 */
    beforeLoad?: () => Promise<void>;
}

function definedOnly(props: Record<string, unknown>): TrackProps {
    return Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

/** 字段取值与 Web 一致，环境值域为 Home 的 dev/staging/prod。 */
const HOST_EVENT_METADATA = definedOnly({
    app_version: process.env.NEXT_PUBLIC_APP_VERSION?.trim(),
    deployment_environment: process.env.NEXT_PUBLIC_DEPLOY_ENV?.trim(),
});

/** 首触 UTM 归因沿用 Web 的 `custom_utm_*` 字段名，只在授权后注册。 */
function readUtmSuperProperties(): TrackProps | null {
    const utm = readUtmCookie();
    if (!utm) return null;
    return definedOnly({
        custom_utm_source: utm.utm_source,
        custom_utm_medium: utm.utm_medium,
        custom_utm_campaign: utm.utm_campaign,
        custom_utm_term: utm.utm_term,
        custom_utm_content: utm.utm_content,
        custom_utm_link_id: utm.link_id,
    });
}

/** 配置按文档固定；只有 Provider 生命周期和 SDK 加载状态，不复制授权状态。 */
export function createMixpanelClient(
    token: string,
    load = () => import('mixpanel-browser').then((module) => module.default),
    { eventMetadata = HOST_EVENT_METADATA, superProperties = readUtmSuperProperties, beforeLoad }: MixpanelClientConfig = {},
) {
    let active = false;
    let sdk: MixpanelApi | null = null;
    let initialized = false;
    let running = false;
    let loading: Promise<void> | null = null;
    let generation = 0;
    let identity: { id: string; props?: TrackProps } | null = null;
    let resetPending = false;
    let anonymousConfirmed = false;
    let queue: Operation[] = [];

    const allowed = () => typeof window !== 'undefined' && active && Boolean(token) && getConsentSnapshot().analyticsAllowed;

    function registerSuperProperties(): void {
        if (!sdk || !initialized || !allowed()) return;
        let props: TrackProps | null = null;
        try {
            props = superProperties();
        } catch {
            console.error('[mixpanel] super properties unavailable');
        }
        if (props && Object.keys(props).length > 0) sdk.register(props);
    }

    function clearIdentity(): void {
        if (!sdk || !initialized) return;
        const reset = resetPending || (anonymousConfirmed && Boolean(sdk.get_property('$user_id')));
        resetPending = false;
        if (!reset) return;
        sdk.reset();
        // reset 会清掉 super properties，身份边界之后重新注册归因。
        registerSuperProperties();
    }

    function applyIdentity(): void {
        if (!sdk || !allowed()) return;
        clearIdentity();
        if (!identity) return;
        sdk.identify(identity.id);
        if (identity.props) sdk.people.set(identity.props);
    }

    function send(operation: Operation, delayed: boolean): boolean {
        if (!sdk || !initialized || !running || !allowed()) return false;
        try {
            if (operation.kind === 'people') sdk.people.set_once(operation.props);
            else if (operation.kind === 'register') sdk.register(operation.props);
            else {
                const payload = {
                    ...operation.props,
                    ...(operation.options?.insertId ? { $insert_id: operation.options.insertId } : {}),
                    ...(delayed ? { time: Math.floor(operation.time / 1000) } : {}),
                };
                if (operation.options?.sendImmediately) sdk.track(operation.name, payload, { send_immediately: true });
                else sdk.track(operation.name, payload);
            }
            return true;
        } catch {
            console.error('[mixpanel] operation failed');
            return false;
        }
    }

    function flush(): void {
        const pending = queue;
        queue = [];
        for (const operation of pending) {
            if (Date.now() - operation.time <= 60_000) send(operation, true);
        }
    }

    function activate(): void {
        if (!sdk || !initialized || !allowed()) return;
        if (!running) {
            sdk.opt_in_tracking({ track: () => {} });
            running = true;
        }
        registerSuperProperties();
        applyIdentity();
        flush();
    }

    function initialize(): Promise<void> {
        if (!allowed()) return Promise.resolve();
        if (initialized) {
            try {
                activate();
            } catch {
                console.error('[mixpanel] activation failed');
            }
            return Promise.resolve();
        }
        if (loading) return loading;
        const epoch = generation;
        const request = (beforeLoad?.() ?? Promise.resolve())
            .then(() => allowed() && epoch === generation ? sdk ?? load() : null)
            .then((api) => {
                if (!api) return;
                sdk = api;
                if (!allowed() || epoch !== generation) return;
                api.init(token, {
                    api_transport: 'sendBeacon',
                    debug: process.env.NEXT_PUBLIC_DEBUG_MODE === 'true',
                    track_pageview: false,
                    autocapture: false,
                    record_sessions_percent: 0,
                    opt_out_tracking_by_default: true,
                    opt_out_persistence_by_default: true,
                });
                initialized = true;
                // 初始化不自行 reset：恢复身份是否有效由身份桥在 Auth 给出权威结论后决定
                // （confirmAnonymous / resetIdentity），否则每次整页加载都会丢设备身份与
                // UTM super properties。
                activate();
            })
            .catch(() => console.error('[mixpanel] initialization failed'))
            .finally(() => {
                if (loading === request) loading = null;
                if (allowed() && epoch !== generation) void initialize();
            });
        loading = request;
        return request;
    }

    function accept(operation: Operation): boolean {
        if (!allowed()) return false;
        if (initialized && running) return send(operation, false);
        if (queue.length === 100) queue.shift();
        queue.push(operation);
        void initialize();
        return true;
    }

    function revokeConsent(): void {
        generation += 1;
        queue = [];
        identity = null;
        if (!running || !sdk) return;
        running = false;
        const options = { delete_user: false, clear_persistence: true };
        try {
            sdk.opt_out_tracking(options);
        } catch {
            console.error('[mixpanel] opt-out failed');
        }
        try {
            sdk.reset();
        } catch {
            console.error('[mixpanel] identity reset failed');
        }
    }

    function syncConsent(): Promise<void> {
        if (!getConsentSnapshot().analyticsAllowed) {
            revokeConsent();
            return Promise.resolve();
        }
        return initialize();
    }

    const logger = (name: string, props?: TrackProps, options?: MixpanelTrackOptions): boolean => {
        if (!name.trim() || (options?.kind === 'terminal' && !options.insertId)) return false;
        const payload = props || Object.keys(eventMetadata).length > 0 ? { ...props, ...eventMetadata } : undefined;
        return accept({ kind: 'event', name, props: payload, options: options && { ...options }, time: Date.now() });
    };
    function resetIdentity(): void {
        generation += 1;
        queue = [];
        identity = null;
        resetPending = true;
        try {
            clearIdentity();
        } catch {
            console.error('[mixpanel] identity reset failed');
        }
    }

    return {
        start(): Promise<void> {
            active = true;
            return syncConsent();
        },
        syncConsent,
        dispose(): void {
            active = false;
            generation += 1;
            queue = [];
            // 解绑不是撤回；保留 SDK 身份、授权及已接纳的授权期事件。
        },
        logger,
        getDistinctId(): string | null {
            if (!sdk || !initialized || !running || !allowed()) return null;
            try {
                return sdk.get_distinct_id?.() || null;
            } catch {
                return null;
            }
        },
        identify(id: string, props?: TrackProps): void {
            if (!allowed() || !id.trim()) return;
            if (identity && identity.id !== id.trim()) resetIdentity();
            identity = { id: id.trim(), props: props && { ...props } };
            anonymousConfirmed = false;
            try {
                if (initialized && running) applyIdentity();
                else void initialize();
            } catch {
                console.error('[mixpanel] identity update failed');
            }
        },
        setPeopleOnce(props: TrackProps): void {
            accept({ kind: 'people', props: { ...props }, time: Date.now() });
        },
        register(props: TrackProps): void {
            accept({ kind: 'register', props: { ...props }, time: Date.now() });
        },
        resetIdentity,
        confirmAnonymous(): void {
            anonymousConfirmed = true;
            try {
                clearIdentity();
            } catch {
                console.error('[mixpanel] restored identity cleanup failed');
            }
        },
    };
}

export const mixpanelClient = createMixpanelClient(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN?.trim() ?? '', undefined, {
    beforeLoad: waitForInitialContent,
});
